import React from 'react';
import { OrderReceipt } from '../types';

interface TerminalViewProps {
  orders: OrderReceipt[];
  leads: string[];
  stripePaymentLink: string;
  onUpdateStripeLink: (link: string) => void;
  onLogManualSale: (receipt: OrderReceipt) => void;
}

export const TerminalView: React.FC<TerminalViewProps> = ({
  orders,
  leads,
  stripePaymentLink,
  onUpdateStripeLink,
  onLogManualSale,
}) => {
  const totalRevenue = orders.reduce((sum, o) => sum + (o.price || 0), 0);

  const handleManualSale = () => {
    const title = prompt('Runbook Title:', 'The Sprint Pricing Architecture');
    if (!title) return;
    const email = prompt('Customer Email:', 'client@firm.com');
    if (!email) return;
    const amount = Number(prompt('Amount ($ USD):', '21')) || 21;

    onLogManualSale({
      id: `tx_${Math.random().toString(36).slice(2, 8)}`,
      title,
      slug: 'sprint-pricing-architecture',
      price: amount,
      email,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      method: 'Offline / Bank Wire',
    });
  };

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs uppercase font-mono tracking-widest text-sky-400">
          LEDGER // FINANCIAL SETTLEMENT
        </p>
        <h1 className="font-tech text-3xl font-bold text-white mt-1">
          Terminal Till
        </h1>
        <p className="text-xs text-[#8b92a5] font-mono mt-1">
          Edge transactions, customer delivery status, and Stripe webhook configuration.
        </p>
      </div>

      {/* Financial Metrics */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="p-5 rounded-xl blueprint-card space-y-1">
          <span className="text-[10px] font-mono uppercase text-[#8b92a5]">
            Total Realized Revenue
          </span>
          <p className="font-mono text-3xl font-bold text-white">
            ${totalRevenue.toFixed(2)}
          </p>
        </div>
        <div className="p-5 rounded-xl blueprint-card space-y-1">
          <span className="text-[10px] font-mono uppercase text-[#8b92a5]">
            Confirmed Orders
          </span>
          <p className="font-mono text-3xl font-bold text-white">
            {orders.length}
          </p>
        </div>
        <div className="p-5 rounded-xl blueprint-card space-y-1">
          <span className="text-[10px] font-mono uppercase text-[#8b92a5]">
            Verified Customer Base
          </span>
          <p className="font-mono text-3xl font-bold text-white">
            {leads.length}
          </p>
        </div>
      </div>

      {/* Stripe Configuration */}
      <div className="p-6 rounded-xl blueprint-card space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-tech text-base font-bold text-white">
              Stripe Edge Checkout Bridge
            </h3>
            <p className="text-xs font-mono text-[#8b92a5]">
              Configure a live Stripe Payment Link or test directly via instant simulator.
            </p>
          </div>
          <span className="px-2 py-0.5 rounded text-[11px] font-mono bg-emerald-500/10 border border-emerald-500/30 text-emerald-400">
            ENGINE STATUS: ONLINE
          </span>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 pt-2 font-mono text-xs">
          <div>
            <label className="text-[10px] text-[#8b92a5] uppercase block">
              Stripe Payment Link URL
            </label>
            <input
              type="text"
              value={stripePaymentLink}
              onChange={(e) => onUpdateStripeLink(e.target.value)}
              placeholder="https://buy.stripe.com/..."
              className="w-full mt-1 px-3 py-2 rounded bg-[#181b26] border border-[#232736] text-white placeholder-zinc-600 focus:outline-none focus:border-sky-400"
            />
          </div>
          <div>
            <label className="text-[10px] text-[#8b92a5] uppercase block">
              Cloudflare Worker Webhook Hook
            </label>
            <input
              type="text"
              readOnly
              value="/api/stripe/webhook"
              className="w-full mt-1 px-3 py-2 rounded bg-[#181b26] border border-[#232736] text-zinc-400"
            />
          </div>
        </div>
      </div>

      {/* Ledger Table */}
      <div className="blueprint-card rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-[#232736] flex items-center justify-between">
          <span className="font-tech text-sm font-bold text-white">Ledger Receipts</span>
          <button
            onClick={handleManualSale}
            className="text-xs font-mono text-sky-400 hover:text-sky-300"
          >
            [+ Log Offline Transfer]
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-mono">
            <thead className="bg-[#181b26] text-[#8b92a5] uppercase border-b border-[#232736]">
              <tr>
                <th className="px-5 py-3">Runbook</th>
                <th className="px-5 py-3">Customer</th>
                <th className="px-5 py-3">Amount</th>
                <th className="px-5 py-3">Method</th>
                <th className="px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#232736] text-zinc-300">
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-6 text-center text-[#8b92a5]">
                    No transactions recorded. Acquire a runbook in the catalog or log an offline transfer.
                  </td>
                </tr>
              ) : (
                orders.map((o) => (
                  <tr key={o.id} className="hover:bg-[#181b26] transition">
                    <td className="px-5 py-3 text-white font-bold">{o.title}</td>
                    <td className="px-5 py-3 text-[#8b92a5]">{o.email}</td>
                    <td className="px-5 py-3 text-sky-400 font-bold">${o.price}</td>
                    <td className="px-5 py-3 text-[#525970]">{o.method}</td>
                    <td className="px-5 py-3">
                      <span className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                        CONFIRMED
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
